import { Time } from 'lightweight-charts'
import { Point } from '../../../components/chartV2/drawingTools/drawing.interface'
import { CHART_PATTERN_DEFAULT_COLOR } from '../../../assets/chartPatternConfig'
import { ChartPattern } from './chartPatternTypes'
import {
    AlternatingSwing,
    buildAlternatingSwings,
    CHART_PATTERN_PIVOT_LENGTH,
    findPivotLows,
    linePriceAt,
    Pivot,
    resolveLookbackBars,
    shouldIncludePriceTarget,
    sliceRecentOhlc,
} from './patternUtils'

/** @deprecated Use ChartPattern with kind HEAD_AND_SHOULDERS */
export type HeadAndShouldersPattern = ChartPattern

export type HeadAndShouldersDetectOptions = {
    trendHeight?: number
    inProgress?: boolean
    patternsFilter?: string
    priceTargetsFilter?: string
    /** Max shoulder price difference as fraction of max shoulder (default 0.2 = 20%). */
    shoulderTolerancePct?: number
    /** Max neckline low difference as fraction (default 0.08 = 8%). */
    necklineTolerancePct?: number
    /** Min head prominence above shoulders as fraction (default 0.01 = 1%). */
    headProminencePct?: number
    /** Min width ratio head-to-LS / RS-to-head (default 0.6). */
    widthRatioMin?: number
    /** Max width ratio head-to-LS / RS-to-head (default 1.6). */
    widthRatioMax?: number
    /** Only return patterns scoring at or above this threshold (default 45). */
    confidenceThreshold?: number
    maxPatterns?: number
    close?: number[]
    volume?: number[]
}

type HeadShoulderCandidate = {
    leftShoulder: AlternatingSwing
    leftNeck: AlternatingSwing
    head: AlternatingSwing
    rightNeck: AlternatingSwing
    rightShoulder: AlternatingSwing
    confidence: number
    necklineSlope: number
    breakoutIndex?: number
    targetStatus: HeadAndShouldersTargetStatus
    inProgress: boolean
}

export type HeadAndShouldersTargetStatus = 'Awaiting' | 'In progress' | 'Reached' | 'Failed'

const toPoint = (id: string, timestampMs: number, price: number): Point => ({
    id,
    x: Math.floor(timestampMs / 1000) as Time,
    y: price,
})

const HS_PIVOT_LENGTH = CHART_PATTERN_PIVOT_LENGTH

function isHeadAndShouldersSequence(swings: AlternatingSwing[]): boolean {
    if (swings.length !== 5) return false
    return (
        swings[0].kind === 'h'
        && swings[1].kind === 'l'
        && swings[2].kind === 'h'
        && swings[3].kind === 'l'
        && swings[4].kind === 'h'
    )
}

function pctDiff(a: number, b: number): number {
    const denom = Math.max(Math.abs(a), Math.abs(b))
    if (!Number.isFinite(denom) || denom === 0) return Infinity
    return Math.abs(a - b) / denom
}

function validateGeometry(
    leftShoulder: AlternatingSwing,
    leftNeck: AlternatingSwing,
    head: AlternatingSwing,
    rightNeck: AlternatingSwing,
    rightShoulder: AlternatingSwing,
    shoulderTolerancePct: number,
    necklineTolerancePct: number,
    headProminencePct: number,
    widthRatioMin: number,
    widthRatioMax: number,
): boolean {
    if (head.price <= leftShoulder.price || head.price <= rightShoulder.price) {
        return false
    }

    const shoulderDiff = pctDiff(leftShoulder.price, rightShoulder.price)
    if (shoulderDiff > shoulderTolerancePct) {
        return false
    }

    const maxShoulder = Math.max(leftShoulder.price, rightShoulder.price)
    if (head.price < maxShoulder * (1 + headProminencePct)) {
        return false
    }

    if (pctDiff(leftNeck.price, rightNeck.price) > necklineTolerancePct) {
        return false
    }

    if (
        leftShoulder.price <= leftNeck.price
        || head.price <= leftNeck.price
        || rightShoulder.price <= rightNeck.price
        || head.price <= rightNeck.price
    ) {
        return false
    }

    const leftWidth = head.index - leftShoulder.index
    const rightWidth = rightShoulder.index - head.index
    if (leftWidth <= 0 || rightWidth <= 0) return false

    const ratio = leftWidth / rightWidth
    if (ratio < widthRatioMin || ratio > widthRatioMax) {
        return false
    }

    return true
}

function scoreShoulderSymmetry(leftPrice: number, rightPrice: number, maxPct: number): number {
    const diff = pctDiff(leftPrice, rightPrice)
    if (diff >= maxPct) return 0
    return 25 * (1 - diff / maxPct)
}

function scoreHeadProminence(headPrice: number, maxShoulder: number, minPct: number): number {
    if (maxShoulder <= 0) return 0
    const prominence = (headPrice - maxShoulder) / maxShoulder
    if (prominence < minPct) return 0
    return Math.min(25, 25 * (prominence / (minPct * 2)))
}

function scoreNecklineSymmetry(leftNeck: number, rightNeck: number, maxPct: number): number {
    const diff = pctDiff(leftNeck, rightNeck)
    if (diff >= maxPct) return 0
    return 20 * (1 - diff / maxPct)
}

function scoreWidthSymmetry(leftWidth: number, rightWidth: number, minRatio: number, maxRatio: number): number {
    if (leftWidth <= 0 || rightWidth <= 0) return 0
    const ratio = leftWidth / rightWidth
    if (ratio < minRatio || ratio > maxRatio) return 0
    const ideal = 1
    const deviation = Math.abs(ratio - ideal) / Math.max(ideal, ratio)
    return Math.max(0, 10 * (1 - deviation))
}

function scoreVolume(
    leftShoulder: AlternatingSwing,
    head: AlternatingSwing,
    rightShoulder: AlternatingSwing,
    volume?: number[],
): number {
    if (!volume?.length) return 10

    const lsVol = volume[leftShoulder.index] ?? 0
    const headVol = volume[head.index] ?? 0
    const rsVol = volume[rightShoulder.index] ?? 0
    if (lsVol <= 0 || headVol <= 0 || rsVol <= 0) return 10

    let score = 20
    if (lsVol <= headVol) score -= 8
    if (headVol <= rsVol) score -= 8
    if (lsVol <= rsVol) score -= 4
    return Math.max(0, score)
}

function findBreakoutIndex(
    close: number[] | undefined,
    low: number[],
    timestamps: number[],
    leftNeck: AlternatingSwing,
    rightNeck: AlternatingSwing,
    afterIdx: number,
): number | undefined {
    for (let i = afterIdx + 1; i < low.length; i++) {
        const neckPrice = linePriceAt(timestamps, leftNeck, rightNeck, i)
        const price = close?.[i] ?? low[i]
        if (price < neckPrice) {
            return i
        }
    }
    return undefined
}

function scoreBreakout(breakoutIndex: number | undefined): number {
    return breakoutIndex != null ? 10 : 0
}

function getTargetStatusFromPrices(
    low: number[],
    close: number[] | undefined,
    timestamps: number[],
    leftNeck: AlternatingSwing,
    rightNeck: AlternatingSwing,
    rightShoulder: AlternatingSwing,
    breakoutIndex: number | undefined,
    priceTarget: number,
    inProgress: boolean,
): HeadAndShouldersTargetStatus {
    if (inProgress) return 'Awaiting'
    if (breakoutIndex == null) return 'Awaiting'

    for (let i = breakoutIndex; i < Math.min(low.length, breakoutIndex + 150); i++) {
        if (low[i] <= priceTarget) {
            return 'Reached'
        }

        const necklinePrice = linePriceAt(timestamps, leftNeck, rightNeck, i)
        const closePrice = close?.[i]
        if (closePrice != null && closePrice > necklinePrice && i > rightShoulder.index) {
            return 'Failed'
        }
    }

    return 'In progress'
}

function computeConfidence(
    leftShoulder: AlternatingSwing,
    leftNeck: AlternatingSwing,
    head: AlternatingSwing,
    rightNeck: AlternatingSwing,
    rightShoulder: AlternatingSwing,
    breakoutIndex: number | undefined,
    volume: number[] | undefined,
    shoulderTolerancePct: number,
    necklineTolerancePct: number,
    headProminencePct: number,
    widthRatioMin: number,
    widthRatioMax: number,
): number {
    const leftWidth = head.index - leftShoulder.index
    const rightWidth = rightShoulder.index - head.index
    const maxShoulder = Math.max(leftShoulder.price, rightShoulder.price)

    const total =
        scoreShoulderSymmetry(leftShoulder.price, rightShoulder.price, shoulderTolerancePct)
        + scoreHeadProminence(head.price, maxShoulder, headProminencePct)
        + scoreNecklineSymmetry(leftNeck.price, rightNeck.price, necklineTolerancePct)
        + scoreWidthSymmetry(leftWidth, rightWidth, widthRatioMin, widthRatioMax)
        + scoreVolume(leftShoulder, head, rightShoulder, volume)
        + scoreBreakout(breakoutIndex)

    return Math.round(Math.min(100, total))
}

function necklineSlope(leftNeck: AlternatingSwing, rightNeck: AlternatingSwing): number {
    const span = rightNeck.index - leftNeck.index
    if (span <= 0) return 0
    return (rightNeck.price - leftNeck.price) / span
}

function findPatternStartPivot(
    pivotLows: Pivot[],
    leftShoulder: AlternatingSwing,
    pivotLength: number,
    low: number[],
): Pivot {
    const before = pivotLows.filter((p) => p.index < leftShoulder.index)
    if (before.length) {
        return before[before.length - 1]
    }
    const fallbackIdx = Math.max(0, leftShoulder.index - pivotLength * 2)
    return { index: fallbackIdx, price: low[fallbackIdx] }
}

function computePoint7(
    timestamps: number[],
    low: number[],
    leftNeck: AlternatingSwing,
    rightNeck: AlternatingSwing,
    rightShoulder: AlternatingSwing,
    breakoutIndex: number | undefined,
    inProgress: boolean,
): Pivot {
    if (breakoutIndex != null) {
        return {
            index: breakoutIndex,
            price: linePriceAt(timestamps, leftNeck, rightNeck, breakoutIndex),
        }
    }

    const tNeckRight = timestamps[rightNeck.index]
    const tShoulder = timestamps[rightShoulder.index]
    const segmentSlope = tShoulder !== tNeckRight
        ? (rightShoulder.price - rightNeck.price) / (tShoulder - tNeckRight)
        : 0

    for (let i = rightShoulder.index + 1; i < timestamps.length; i++) {
        const neckPrice = linePriceAt(timestamps, leftNeck, rightNeck, i)
        const linePrice = rightNeck.price + segmentSlope * (timestamps[i] - tNeckRight)
        if (low[i] <= neckPrice || linePrice <= neckPrice) {
            return { index: i, price: neckPrice }
        }
    }

    const lastIdx = timestamps.length - 1
    if (inProgress && lastIdx > rightShoulder.index) {
        return {
            index: lastIdx,
            price: linePriceAt(timestamps, leftNeck, rightNeck, lastIdx),
        }
    }

    const fallbackIdx = Math.min(timestamps.length - 1, rightShoulder.index + HS_PIVOT_LENGTH)
    return {
        index: fallbackIdx,
        price: linePriceAt(timestamps, leftNeck, rightNeck, fallbackIdx),
    }
}

function refineRightShoulderHigh(
    high: number[],
    head: AlternatingSwing,
    rightNeck: AlternatingSwing,
    rightShoulder: AlternatingSwing,
    breakoutIndex: number | undefined,
    pivotLength: number,
): AlternatingSwing {
    const start = rightNeck.index + 1
    const end = breakoutIndex != null
        ? Math.max(start, breakoutIndex - 1)
        : Math.min(high.length - 1, rightShoulder.index + pivotLength * 2)

    let best = rightShoulder
    for (let i = start; i <= end; i++) {
        const price = high[i]
        if (!Number.isFinite(price)) continue
        if (price >= head.price) continue
        if (price <= rightNeck.price) continue
        if (price > best.price || (price === best.price && i > best.index)) {
            best = { index: i, price, kind: 'h' }
        }
    }
    return best
}

function buildPattern(
    timestamps: number[],
    pivotLows: Pivot[],
    high: number[],
    low: number[],
    candidate: HeadShoulderCandidate,
    pivotLength: number,
    includePriceTarget: boolean,
): ChartPattern {
    const { leftShoulder, leftNeck, head, rightNeck, rightShoulder } = candidate
    const refinedRightShoulder = refineRightShoulderHigh(
        high,
        head,
        rightNeck,
        rightShoulder,
        candidate.breakoutIndex,
        pivotLength,
    )
    const point1 = findPatternStartPivot(pivotLows, leftShoulder, pivotLength, low)
    const point7 = computePoint7(
        timestamps,
        low,
        leftNeck,
        rightNeck,
        refinedRightShoulder,
        candidate.breakoutIndex,
        candidate.inProgress,
    )
    const necklineAtHead = linePriceAt(timestamps, leftNeck, rightNeck, head.index)
    const patternHeight = head.price - necklineAtHead
    const patternInProgress = candidate.inProgress
    const color = CHART_PATTERN_DEFAULT_COLOR

    return {
        kind: 'HEAD_AND_SHOULDERS',
        bias: 'bearish',
        points: [
            toPoint('a', timestamps[point1.index], point1.price),
            toPoint('b', timestamps[leftShoulder.index], leftShoulder.price),
            toPoint('c', timestamps[leftNeck.index], leftNeck.price),
            toPoint('d', timestamps[head.index], head.price),
            toPoint('e', timestamps[rightNeck.index], rightNeck.price),
            toPoint('f', timestamps[refinedRightShoulder.index], refinedRightShoulder.price),
            toPoint('g', timestamps[point7.index], point7.price),
        ],
        priceLineSegments: [
            { from: 'a', to: 'b', style: 'solid', color },
            { from: 'b', to: 'c', style: 'solid', color },
            { from: 'c', to: 'd', style: 'solid', color },
            { from: 'd', to: 'e', style: 'solid', color },
            { from: 'e', to: 'f', style: 'solid', color },
            { from: 'f', to: 'g', style: patternInProgress ? 'dotted' : 'solid', color },
        ],
        boundaryLines: [{ from: 'c', to: 'e', style: 'dotted', color }],
        fillTriangles: [['c', 'd', 'e']],
        fillBackgroundColor: color,
        fillBackgroundOpacity: 0.2,
        labels: [
            { pointId: 'b', text: 'Left Shoulder', position: 'above', color },
            { pointId: 'd', text: 'Head', position: 'above', color },
            { pointId: 'f', text: 'Right Shoulder', position: 'above', color },
        ],
        priceTarget: includePriceTarget ? point7.price - patternHeight : undefined,
        targetAnchorPointId: 'g',
        inProgress: patternInProgress,
    }
}

function evaluateCandidate(
    leftShoulder: AlternatingSwing,
    leftNeck: AlternatingSwing,
    head: AlternatingSwing,
    rightNeck: AlternatingSwing,
    rightShoulder: AlternatingSwing,
    close: number[] | undefined,
    low: number[],
    timestamps: number[],
    volume: number[] | undefined,
    shoulderTolerancePct: number,
    necklineTolerancePct: number,
    headProminencePct: number,
    widthRatioMin: number,
    widthRatioMax: number,
    inProgress: boolean,
): HeadShoulderCandidate | null {
    if (!validateGeometry(
        leftShoulder,
        leftNeck,
        head,
        rightNeck,
        rightShoulder,
        shoulderTolerancePct,
        necklineTolerancePct,
        headProminencePct,
        widthRatioMin,
        widthRatioMax,
    )) {
        return null
    }

    const breakoutIndex = inProgress
        ? undefined
        : findBreakoutIndex(close, low, timestamps, leftNeck, rightNeck, rightShoulder.index)

    const confidence = computeConfidence(
        leftShoulder,
        leftNeck,
        head,
        rightNeck,
        rightShoulder,
        breakoutIndex,
        volume,
        shoulderTolerancePct,
        necklineTolerancePct,
        headProminencePct,
        widthRatioMin,
        widthRatioMax,
    )
    const necklineAtHead = linePriceAt(timestamps, leftNeck, rightNeck, head.index)
    const patternHeight = head.price - necklineAtHead
    const breakoutPrice = breakoutIndex == null
        ? linePriceAt(timestamps, leftNeck, rightNeck, rightShoulder.index)
        : linePriceAt(timestamps, leftNeck, rightNeck, breakoutIndex)
    const targetStatus = getTargetStatusFromPrices(
        low,
        close,
        timestamps,
        leftNeck,
        rightNeck,
        rightShoulder,
        breakoutIndex,
        breakoutPrice - patternHeight,
        inProgress,
    )

    return {
        leftShoulder,
        leftNeck,
        head,
        rightNeck,
        rightShoulder,
        confidence,
        necklineSlope: necklineSlope(leftNeck, rightNeck),
        breakoutIndex,
        targetStatus,
        inProgress,
    }
}

function scanCompletedPatterns(
    swings: AlternatingSwing[],
    close: number[] | undefined,
    low: number[],
    timestamps: number[],
    volume: number[] | undefined,
    shoulderTolerancePct: number,
    necklineTolerancePct: number,
    headProminencePct: number,
    widthRatioMin: number,
    widthRatioMax: number,
    confidenceThreshold: number,
): HeadShoulderCandidate[] {
    const candidates: HeadShoulderCandidate[] = []

    for (let i = 0; i <= swings.length - 5; i++) {
        const window = swings.slice(i, i + 5)
        if (!isHeadAndShouldersSequence(window)) continue

        const [leftShoulder, leftNeck, head, rightNeck, rightShoulder] = window
        const candidate = evaluateCandidate(
            leftShoulder,
            leftNeck,
            head,
            rightNeck,
            rightShoulder,
            close,
            low,
            timestamps,
            volume,
            shoulderTolerancePct,
            necklineTolerancePct,
            headProminencePct,
            widthRatioMin,
            widthRatioMax,
            false,
        )
        if (candidate && candidate.confidence >= confidenceThreshold) {
            candidates.push(candidate)
        }
    }

    return candidates
}

function findFormingRightShoulder(
    high: number[],
    head: AlternatingSwing,
    rightNeck: AlternatingSwing,
    pivotLength: number,
): AlternatingSwing | null {
    const searchStart = rightNeck.index + 1
    const searchEnd = high.length - 1
    if (searchStart > searchEnd) return null

    let best: AlternatingSwing | null = null
    for (let i = searchStart; i <= searchEnd; i++) {
        if (high[i] >= head.price) continue
        if (high[i] <= rightNeck.price) continue

        let isPivotHigh = true
        const left = Math.max(0, i - pivotLength)
        const right = Math.min(high.length - 1, i + pivotLength)
        for (let j = left; j <= right; j++) {
            if (j !== i && high[j] >= high[i]) {
                isPivotHigh = false
                break
            }
        }

        if (!isPivotHigh && i < searchEnd) continue
        if (!best || high[i] > best.price || (high[i] === best.price && i > best.index)) {
            best = { index: i, price: high[i], kind: 'h' }
        }
    }

    return best
}

function detectInProgressPattern(
    swings: AlternatingSwing[],
    high: number[],
    low: number[],
    timestamps: number[],
    volume: number[] | undefined,
    pivotLength: number,
    shoulderTolerancePct: number,
    necklineTolerancePct: number,
    headProminencePct: number,
    widthRatioMin: number,
    widthRatioMax: number,
    confidenceThreshold: number,
): HeadShoulderCandidate | null {
    if (swings.length < 4) return null

    const tail = swings.slice(-4)
    if (tail[0].kind !== 'h' || tail[1].kind !== 'l' || tail[2].kind !== 'h' || tail[3].kind !== 'l') {
        return null
    }

    const [leftShoulder, leftNeck, head, rightNeck] = tail
    const rightShoulder = findFormingRightShoulder(high, head, rightNeck, pivotLength)
    if (!rightShoulder) return null

    const relaxedShoulder = shoulderTolerancePct * 1.5
    const relaxedNeck = necklineTolerancePct * 1.5

    const candidate = evaluateCandidate(
        leftShoulder,
        leftNeck,
        head,
        rightNeck,
        rightShoulder,
        undefined,
        low,
        timestamps,
        volume,
        relaxedShoulder,
        relaxedNeck,
        headProminencePct * 0.8,
        widthRatioMin * 0.85,
        widthRatioMax * 1.15,
        true,
    )

    if (!candidate || candidate.confidence < confidenceThreshold * 0.75) {
        return null
    }

    return candidate
}

function candidatesOverlap(a: HeadShoulderCandidate, b: HeadShoulderCandidate): boolean {
    const aStart = a.leftShoulder.index
    const aEnd = a.breakoutIndex ?? a.rightShoulder.index
    const bStart = b.leftShoulder.index
    const bEnd = b.breakoutIndex ?? b.rightShoulder.index
    return !(aEnd < bStart || bEnd < aStart)
}

function dedupeCandidates(candidates: HeadShoulderCandidate[]): HeadShoulderCandidate[] {
    const sorted = [...candidates].sort((a, b) => {
        if (b.confidence !== a.confidence) return b.confidence - a.confidence
        return b.rightShoulder.index - a.rightShoulder.index
    })

    const kept: HeadShoulderCandidate[] = []
    for (const candidate of sorted) {
        if (kept.some((existing) => candidatesOverlap(existing, candidate))) {
            continue
        }
        kept.push(candidate)
    }
    return kept
}

function normalizeTolerancePct(value: number | undefined, defaultFraction: number): number {
    if (value == null) return defaultFraction
    return value >= 1 ? value / 100 : value
}

function filterCandidatesByPatternStatus(
    candidates: HeadShoulderCandidate[],
    patternsFilter: string,
): HeadShoulderCandidate[] {
    if (patternsFilter === 'Only Awaiting') {
        return candidates.filter((candidate) => candidate.targetStatus === 'Awaiting' || candidate.targetStatus === 'In progress')
    }

    if (patternsFilter === 'Awaiting and Reached') {
        return candidates.filter((candidate) => candidate.targetStatus !== 'Failed')
    }

    if (patternsFilter === 'Last Awaiting') {
        const awaiting = candidates
            .filter((candidate) => candidate.targetStatus === 'Awaiting' || candidate.targetStatus === 'In progress')
            .sort((a, b) => (b.breakoutIndex ?? b.rightShoulder.index) - (a.breakoutIndex ?? a.rightShoulder.index))
        return awaiting.length ? [awaiting[0]] : []
    }

    return candidates
}

/**
 * Detects bearish head-and-shoulders patterns using pivot-sequence scanning (H-L-H-L-H),
 * geometric validation, neckline breakout confirmation, and confidence scoring.
 * Returns patterns ordered from most recent to oldest.
 */
export function detectHeadAndShoulders(
    high: number[],
    low: number[],
    timestamps: number[],
    options: HeadAndShouldersDetectOptions = {}
): ChartPattern[] {
    const {
        trendHeight = 50,
        inProgress = true,
        patternsFilter = 'All',
        priceTargetsFilter = 'All',
        shoulderTolerancePct: shoulderToleranceRaw = 20,
        necklineTolerancePct: necklineToleranceRaw = 8,
        headProminencePct: headProminenceRaw = 1,
        widthRatioMin = 0.6,
        widthRatioMax = 1.6,
        confidenceThreshold = 45,
        maxPatterns = 1,
        close,
        volume,
    } = options

    if (patternsFilter === 'Inverse Head and Shoulders') {
        return []
    }

    const shoulderTolerancePct = normalizeTolerancePct(shoulderToleranceRaw, 0.03)
    const necklineTolerancePct = normalizeTolerancePct(necklineToleranceRaw, 0.03)
    const headProminencePct = normalizeTolerancePct(headProminenceRaw, 0.03)

    const includePriceTarget = shouldIncludePriceTarget(priceTargetsFilter)

    const lookbackBars = resolveLookbackBars(trendHeight)
    const sliced = sliceRecentOhlc(high, low, timestamps, lookbackBars)
    high = sliced.high
    low = sliced.low
    timestamps = sliced.timestamps

    const closeSlice = close && close.length > lookbackBars
        ? close.slice(close.length - high.length)
        : close
    const volumeSlice = volume && volume.length > lookbackBars
        ? volume.slice(volume.length - high.length)
        : volume

    const pivotLength = HS_PIVOT_LENGTH
    if (high.length < pivotLength * 6 || low.length !== high.length || timestamps.length !== high.length) {
        return []
    }

    const swings = buildAlternatingSwings(high, low, pivotLength)
    const pivotLows = findPivotLows(low, pivotLength, pivotLength)

    let candidates = scanCompletedPatterns(
        swings,
        closeSlice,
        low,
        timestamps,
        volumeSlice,
        shoulderTolerancePct,
        necklineTolerancePct,
        headProminencePct,
        widthRatioMin,
        widthRatioMax,
        confidenceThreshold,
    )

    if (inProgress) {
        const forming = detectInProgressPattern(
            swings,
            high,
            low,
            timestamps,
            volumeSlice,
            pivotLength,
            shoulderTolerancePct,
            necklineTolerancePct,
            headProminencePct,
            widthRatioMin,
            widthRatioMax,
            confidenceThreshold,
        )
        if (forming) {
            candidates.push(forming)
        }
    }

    candidates = filterCandidatesByPatternStatus(dedupeCandidates(candidates), patternsFilter)

    const patterns = candidates
        .map((candidate) => buildPattern(
            timestamps,
            pivotLows,
            high,
            low,
            candidate,
            pivotLength,
            includePriceTarget,
        ))
        .sort((a, b) => {
            const aTime = (a.points[6]?.x as number) ?? 0
            const bTime = (b.points[6]?.x as number) ?? 0
            return bTime - aTime
        })

    if (maxPatterns > 0) {
        return patterns.slice(0, maxPatterns)
    }
    return patterns
}

export function getHeadAndShouldersTargetStatus(
    points: Point[],
    priceTarget: number | undefined,
    candles: Array<{ time: Time; close: number; low: number }>,
    inProgress = false
): HeadAndShouldersTargetStatus {
    if (inProgress) {
        return 'Awaiting'
    }

    if (priceTarget == null || !Number.isFinite(priceTarget)) {
        return 'Awaiting'
    }

    const rightShoulder = points[5]
    const necklineEnd = points[6]
    if (!rightShoulder || !necklineEnd) {
        return 'Awaiting'
    }

    const necklineTime = necklineEnd.x as number
    const afterPattern = candles
        .filter((candle) => (candle.time as number) >= necklineTime)
        .slice(0, 150)
    if (!afterPattern.length) {
        return 'Awaiting'
    }

    if (afterPattern.some((candle) => candle.low <= priceTarget)) {
        return 'Reached'
    }

    const necklinePrice = necklineEnd.y
    const brokeNeckline = afterPattern.some((candle) => candle.close < necklinePrice)
    if (!brokeNeckline) {
        return 'Awaiting'
    }

    if (afterPattern.some((candle) => candle.close > necklinePrice)) {
        return 'Failed'
    }

    return 'In progress'
}
